// DEV-ONLY dev-user switcher — a small, clearly dev-styled fixed-corner
// control to hop between seeded users (gm / a Müdür / a Personel) and watch
// the scope tiers change. Renders NOTHING when dev-auth is inactive (so it's
// absent from production builds — the whole subtree tree-shakes out behind
// isDevAuthActive()).
//
// On change it calls selectDevUser(): persist the choice → clear the stored
// OIDC user → reload, which re-runs bootstrapDevAuth() as the new user.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import {
  fetchDevUsers,
  getSelectedDevUserKey,
  isDevAuthActive,
  selectDevUser,
  type DevLoginUserSummary,
} from "@/auth/dev-auth";

export function DevUserSwitcher(): React.ReactElement | null {
  const { t } = useTranslation();
  const [users, setUsers] = useState<readonly DevLoginUserSummary[]>([]);
  const [selected, setSelected] = useState<string>(() => getSelectedDevUserKey());

  useEffect(() => {
    if (!isDevAuthActive()) return;
    let cancelled = false;
    void fetchDevUsers().then((rows) => {
      if (!cancelled) setUsers(rows);
    });
    return (): void => {
      cancelled = true;
    };
  }, []);

  if (!isDevAuthActive()) return null;

  return (
    <div
      className={cn(
        "fixed bottom-3 right-3 z-50 flex items-center gap-2 rounded-md",
        "border border-dashed border-[var(--color-border)] bg-[var(--color-card)]",
        "px-2 py-1 text-xs shadow-md",
      )}
    >
      <span
        className={cn(
          "rounded px-1.5 py-0.5 font-mono font-semibold",
          "bg-[var(--color-accent)] text-[var(--color-accent-foreground)]",
        )}
      >
        {t("auth.dev.badge")}
      </span>
      <select
        aria-label={t("auth.dev.switcher_label")}
        value={selected}
        onChange={(e): void => {
          setSelected(e.target.value);
          selectDevUser(e.target.value);
        }}
        className={cn(
          "rounded border border-[var(--color-border)] bg-[var(--color-background)]",
          "px-1 py-0.5 text-xs text-[var(--color-foreground)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]",
        )}
      >
        {users.length === 0 ? (
          <option value={selected}>{t("auth.dev.loading")}</option>
        ) : (
          users.map((u) => (
            <option key={u.key} value={u.key}>
              {u.name}
            </option>
          ))
        )}
      </select>
    </div>
  );
}
